import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { get } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { SHORTLIST_DEAL_INTEREST, SEND_NDA_TO_RECEIVING_PARTY } from '../../../graphql/mutations/dealMutations';
import { openProfileDrawer } from './profileDrawerHelper';
import DealInterestCard from './DealInterestCard';
import DealInterestListItem from './DealInterestListItem';
import { extractDealId } from '../../../utils/dealUtils';

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

const DealShortlisted = ({ deal, refetchDeal, activeFilter = 'all', currentUser }) => {
  const intl = useIntl();
  const [shortlistDealInterest] = useMutation(SHORTLIST_DEAL_INTEREST);
  const [sendNdaToReceivingParty] = useMutation(SEND_NDA_TO_RECEIVING_PARTY);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('grid');
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });
  
  const companyData = settingsData?.company || currentUser?.company || {};
  const agentWord = (intl.locale === 'fr' ? (companyData.agent_word_fr || 'conseiller') : (companyData.agent_word || 'advisor')).toLowerCase();
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLower = agentWordPlural.toLowerCase();
  const agentWordPluralLabel = agentWordPlural.charAt(0).toUpperCase() + agentWordPlural.slice(1);

  // Get allowShortlistMessaging setting from company settings
  const allowShortlistMessaging = get(currentUser, 'company.deal_room_allow_messaging_when_shortlisted', false);

  // Helper function to get advisor type for badge display
  const getAdvisorBadgeInfo = (dealInterest) => {
    // First check if it's shortlisted (this will be the primary badge)
    if (dealInterest.workflow_state === 'shortlisted') {
      return { text: intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_shortlisted' }), color: 'yellow' };
    } else if (dealInterest.workflow_state === 'declined') {
      return { text: intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined' }), color: 'red' };
    }
    
    // Then check for the original type
    if (dealInterest.workflow_state === 'following') {
      return { text: 'Rolodex', color: 'blue' };
    } else if (dealInterest.workflow_state === 'follower') {
      return { text: 'Follower', color: 'purple' };
    }
    
    // Check for suggested match
    if (dealInterest.reason_for_suggestion) {
      return { text: 'Suggested Match', color: 'green' };
    }
    
    return null;
  };

  // Filter shortlisted interests based on selected filter and search
  const filteredShortlisted = () => {
    if (!deal?.shortlisted) return [];
    
    let shortlisted = [...deal.shortlisted];
    
    // Apply filter if it's not 'all'
    if (activeFilter === 'declined') {
      shortlisted = shortlisted.filter(interest => interest.workflow_state === 'declined');
    }
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      shortlisted = shortlisted.filter(
        interest => interest?.inventory_user?.user && (
          (interest.inventory_user.user.name || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.city || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.province || '').toLowerCase().includes(term)
        )
      );
    }
    
    return shortlisted;
  };
  
  const shortlistedAdvisors = filteredShortlisted();

  const handleRemoveFromShortlist = async (dealInterestId) => {
    if (!deal) {
      console.error('Error removing from shortlist: Deal is undefined');
      alert('Error removing from shortlist: Unable to find deal information');
      return;
    }
    
    // Use extractDealId to get the correct deal ID
    const dealIdValue = extractDealId({ deal }, null, true);
    
    if (!dealIdValue) {
      console.error('Error removing from shortlist: Deal ID is missing', {
        dealObj: deal,
        dealInterestId
      });
      alert('Error removing from shortlist: Deal ID is missing');
      return;
    }
    
    try {
      await shortlistDealInterest({
        variables: {
          dealId: dealIdValue,
          dealInterestId
        }
      });
      
      refetchDeal();
      
      // Navigate to reviewing section after unshortlisting
      setTimeout(() => {
        window.location.href = `/succession/deals/${deal.deal_name}/prospects/reviewing`;
      }, 1000);
    } catch (error) {
      console.error('Error removing from shortlist:', error);
      alert('Error removing from shortlist: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSendNda = async (dealInterestId) => {
    if (!deal) {
      console.error('Error sending NDA: Deal is undefined');
      alert('Error sending NDA: Unable to find deal information');
      return;
    }
    
    console.log('Deal structure in DealShortlisted:', deal);
    
    // Use extractDealId to get the correct deal ID
    const dealIdValue = extractDealId({ deal }, null, true);
    
    if (!dealIdValue) {
      console.error('Error sending NDA: Deal ID is missing', {
        dealObj: deal,
        dealIdValue,
        dealInterestId
      });
      alert('Error sending NDA: Deal ID is missing');
      return;
    }
    
    console.log(`Sending NDA for deal "${dealIdValue}" and interest "${dealInterestId}"`);
    
    try {
      const response = await sendNdaToReceivingParty({
        variables: {
          dealId: dealIdValue,
          dealInterestId
        }
      });
      
      console.log('NDA sending response (DealShortlisted):', response);
      
      if (response.data?.sendNdaToReceivingParty?.dealInterest) {
        console.log('NDA sent successfully');
        alert(intl.formatMessage({ id: 'succession.profile_drawer.deal_section.toast_nda_sent' }));
        
        // Trigger data refresh
        console.log('DealShortlisted: NDA sent successfully, refreshing data');
        refetchDeal();
        
        // Navigate to NDAs section after sending NDA
        setTimeout(() => {
          window.location.href = `/succession/deals/${deal.deal_name}/prospects/ndas`;
        }, 1000);
      } else if (response.data?.sendNdaToReceivingParty?.errors?.length) {
        const errors = response.data.sendNdaToReceivingParty.errors;
        console.error('Error from API when sending NDA:', errors);
        
        // Look for specific error messages we can handle more gracefully
        if (errors.some(err => err.includes("Deal not found"))) {
          console.error("The deal ID was not found in the database. Sent ID:", dealIdValue);
          alert("Error: The specified deal could not be found. Please contact support.");
        } else {
          alert(`Error sending NDA: ${errors.join(", ")}`);
        }
      }
    } catch (error) {
      console.error('Exception when sending NDA:', error);
      alert('Error sending NDA: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div>
      {/* Search and View Options */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-succession-text-primary mb-4 md:mb-0">
          {intl.formatMessage(
            {
              id: 'succession_deals.wizard.steps.deal_shortlisted.title_with_agent_word',
              defaultMessage: 'Shortlisted {agentWordPlural}'
            },
            { agentWordPlural: agentWordPluralLabel }
          )} ({shortlistedAdvisors.length})
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative" style={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder={intl.formatMessage(
                {
                  id: 'succession_deals.wizard.steps.deal_shortlisted.search_placeholder_with_agent_word',
                  defaultMessage: 'Search {agentWordPlural}...'
                },
                { agentWordPlural: agentWordPluralLower }
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderRadius: '9999px',
                backgroundColor: 'var(--succession-bg-secondary, #f8fafc)',
                border: '1px solid var(--succession-border, #64748b)',
                color: 'var(--succession-text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--succession-primary, #5489B6)';
                e.target.style.boxShadow = '0 0 0 3px rgba(84, 137, 182, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--succession-border, #64748b)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div className="absolute left-3 top-3 text-succession-text-muted" style={{ pointerEvents: 'none' }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex border border-succession-border rounded-lg overflow-hidden flex-shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 flex items-center ${view === 'grid' ? 'bg-succession-bg-secondary text-succession-primary' : 'text-succession-text-muted'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_shortlisted.view_options.grid" defaultMessage="Grid" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 flex items-center ${view === 'list' ? 'bg-succession-bg-secondary text-succession-primary' : 'text-succession-text-muted'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_shortlisted.view_options.list" defaultMessage="List" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Stage description */}
      <div className="bg-succession-primary-10 border-l-4 border-succession-primary rounded-md p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-primary" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <div className="mt-2 text-sm text-succession-text-secondary">
              <p>
                {intl.formatMessage(
                  {
                    id: 'succession_deals.wizard.steps.deal_shortlisted.stage_title_with_agent_word',
                    defaultMessage: 'Shortlisted {agentWordPlural}:'
                  },
                  { agentWordPlural: agentWordPluralLabel }
                )}
                &nbsp;
                {shortlistedAdvisors.length === 0 
                  ? activeFilter === 'declined'
                    ? intl.formatMessage(
                        {
                          id: 'succession_deals.wizard.steps.deal_shortlisted.stage_empty_declined_with_agent_word',
                          defaultMessage: 'You have no declined shortlisted {agentWordPlural}.'
                        },
                        { agentWordPlural: agentWordPluralLower }
                      )
                    : intl.formatMessage(
                        {
                          id: 'succession_deals.wizard.steps.deal_shortlisted.stage_empty_with_agent_word',
                          defaultMessage: 'You have no shortlisted {agentWordPlural}.'
                        },
                        { agentWordPlural: agentWordPluralLower }
                      )
                  : intl.formatMessage(
                      {
                        id: 'succession_deals.wizard.steps.deal_shortlisted.stage_count_with_agent_word',
                        defaultMessage: 'You have {count} shortlisted {agentWordPlural}.'
                      },
                      {
                        count: shortlistedAdvisors.length,
                        agentWordPlural: agentWordPluralLower
                      }
                    )}
                {activeFilter === 'declined' && shortlistedAdvisors.length > 0 && (
                  <FormattedMessage
                    id="succession_deals.wizard.steps.deal_shortlisted.stage_declined_only_with_agent_word"
                    defaultMessage=" Showing declined {agentWordPlural} only."
                    values={{ agentWordPlural: agentWordPluralLower }}
                  />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {shortlistedAdvisors.length === 0 ? (
        <div className="bg-succession-bg-secondary rounded-lg p-10 text-center">
          <div className="flex flex-col items-center justify-center">
            <svg className="mx-auto h-12 w-12 text-succession-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-succession-text-primary">
              {intl.formatMessage(
                {
                  id: 'succession_deals.wizard.steps.deal_shortlisted.empty_state.title_with_agent_word',
                  defaultMessage: 'No shortlisted {agentWordPlural}'
                },
                { agentWordPlural: agentWordPluralLower }
              )}
            </h3>
            <p className="mt-1 text-sm text-succession-text-muted">
              {activeFilter === 'declined'
                ? intl.formatMessage(
                    {
                      id: 'succession_deals.wizard.steps.deal_shortlisted.empty_state.declined_with_agent_word',
                      defaultMessage: 'There are no declined shortlisted {agentWordPlural}.'
                    },
                    { agentWordPlural: agentWordPluralLower }
                  )
                : intl.formatMessage(
                    {
                      id: 'succession_deals.wizard.steps.deal_shortlisted.empty_state.description_with_agent_word',
                      defaultMessage: 'Browse potential matches and shortlist {agentWordPlural} you\'d like to engage with.'
                    },
                    { agentWordPlural: agentWordPluralLower }
                  )}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {view === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shortlistedAdvisors.map(dealInterest => {
                const badgeInfo = getAdvisorBadgeInfo(dealInterest);
                console.log('Rendering DealInterestCard for:', dealInterest.id, 'with deal:', deal.deal_name);
                return (
                  <DealInterestCard
                    key={dealInterest.id}
                    dealInterest={dealInterest}
                    dealName={deal.deal_name}
                    companySettings={companyData}
                    onRemoveFromShortlist={handleRemoveFromShortlist}
                    onSendNda={handleSendNda}
                    badgeText={badgeInfo?.text}
                    badgeColor={badgeInfo?.color}
                    allowShortlistMessaging={allowShortlistMessaging}
                    onViewDetails={() => {
                      if (dealInterest?.inventory_user?.user?.fb_user_name) {
                        openProfileDrawer(dealInterest.inventory_user.user.fb_user_name, { fromDeal: true, dealInterest });
                      }
                    }}
                    viewDetailsLabel={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_shortlisted.view_profile', defaultMessage: 'View Profile' })}
                  />
                );
              })}
            </div>
          )}
          
          {/* List View */}
          {view === 'list' && (
            <div className="overflow-x-auto border border-succession-border rounded-lg">
              <table className="min-w-full divide-y divide-succession-border">
                <thead className="bg-succession-bg-secondary">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider w-80">{agentWordLabel}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">Location</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Assets</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Revenue</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shortlistedAdvisors.map(dealInterest => {
                    const badgeInfo = getAdvisorBadgeInfo(dealInterest);
                    return (
                      <DealInterestListItem
                        key={dealInterest.id}
                        dealInterest={dealInterest}
                        dealName={deal.deal_name}
                        companySettings={companyData}
                        onRemoveFromShortlist={handleRemoveFromShortlist}
                        onSendNda={handleSendNda}
                        badgeText={badgeInfo?.text}
                        badgeColor={badgeInfo?.color}
                        allowShortlistMessaging={allowShortlistMessaging}
                        onViewDetails={() => {
                          if (dealInterest?.inventory_user?.user?.fb_user_name) {
                            openProfileDrawer(dealInterest.inventory_user.user.fb_user_name, { fromDeal: true, dealInterest });
                          }
                        }}
                        viewDetailsLabel={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_shortlisted.view_profile', defaultMessage: 'View Profile' })}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DealShortlisted.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func.isRequired,
  activeFilter: PropTypes.string,
  currentUser: PropTypes.object
};

export default DealShortlisted; 